// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react'
import { observer } from 'mobx-react'
import { Button, Form, Message } from 'semantic-ui-react'

import { store } from 'services/state'
import {
  customerIdFromApiToken,
  funderIdFromApiToken
} from 'services/premium-quote-payload'
import {
  DEFAULT_WIZARD_OPTIONS,
  generateProposalRequest,
  loadPersistedProposalIds,
  persistProposalIds,
  wizardEnumOptions
} from 'services/proposal-submission-payload'
import { generateLixiProposalPackage } from 'services/lixi-proposal-submission-payload'
import {
  applyJsonToLixiProposalTryItOut,
  applyJsonToProposalTryItOut
} from 'services/swagger-try-it-out'

/**
 * Guided Proposal Submission request builder shown after Authorize and before Default.
 * Instruction used: scope-discipline.instructions.md
 */

const CONSTRUCTION_STATUSES = ['CH', 'CX', 'CO', 'CS']

function initialFormState() {
  const persisted = loadPersistedProposalIds(store.username)
  const customerId = customerIdFromApiToken(store.idToken)
  const funderId = funderIdFromApiToken(store.idToken)
  return Object.assign({}, DEFAULT_WIZARD_OPTIONS, persisted, {
    customerId: customerId,
    funderId: funderId != null ? funderId : '',
    originatorId: persisted.originatorId != null ? String(persisted.originatorId) : ''
  })
}

export default observer(class ProposalSubmissionPayloadWizard extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      form: initialFormState(),
      preview: '',
      errors: [],
      notice: '',
      noticePositive: false
    }
  }

  componentDidUpdate() {
    const nextCustomerId = customerIdFromApiToken(store.idToken)
    const nextFunderId = funderIdFromApiToken(store.idToken)
    const persisted = loadPersistedProposalIds(store.username)
    const nextForm = Object.assign({}, this.state.form)
    let changed = false
    if (nextCustomerId != null && nextCustomerId !== this.state.form.customerId) {
      nextForm.customerId = nextCustomerId
      changed = true
    }
    if (nextFunderId != null && nextFunderId !== this.state.form.funderId) {
      nextForm.funderId = nextFunderId
      changed = true
    }
    if (!this.state.form.originatorId && persisted.originatorId != null) {
      nextForm.originatorId = String(persisted.originatorId)
      changed = true
    }
    if (changed) this.setState({ form: nextForm })
  }

  handleChange = (event, data) => {
    const name = data.name
    const value = typeof data.checked === 'boolean' ? data.checked : data.value
    this.setState({
      form: Object.assign({}, this.state.form, { [name]: value }),
      notice: ''
    })
  }

  handleGenerate = () => {
    persistProposalIds(store.username, {
      funderId: this.state.form.funderId,
      originatorId: this.state.form.originatorId
    })
    const generate = this.props.variant === 'lixi' ? generateLixiProposalPackage : generateProposalRequest
    const result = generate(this.state.form)
    this.setState({
      preview: JSON.stringify(result.payload, null, 2),
      errors: result.errors,
      notice: result.errors.length ? '' : 'Payload generated.',
      noticePositive: result.errors.length === 0
    })
  }

  handleApply = () => {
    if (!this.state.preview) {
      this.setState({ notice: 'Generate a payload first.', noticePositive: false, errors: [] })
      return
    }
    if (this.state.errors.length) {
      this.setState({ notice: 'Fix the errors before applying to Try it out.', noticePositive: false })
      return
    }
    const apply = this.props.variant === 'lixi' ? applyJsonToLixiProposalTryItOut : applyJsonToProposalTryItOut
    const result = apply(this.state.preview)
    this.setState({
      notice: result.ok ? 'Applied to Try it out.' : result.reason,
      noticePositive: result.ok
    })
  }

  handleCopy = () => {
    if (!this.state.preview) {
      this.setState({ notice: 'Generate a payload first.', noticePositive: false, errors: [] })
      return
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(this.state.preview).then(() => {
        this.setState({ notice: 'Copied JSON to clipboard.', noticePositive: true })
      }).catch(() => {
        this.setState({ notice: 'Could not copy. Select the preview and copy it manually.', noticePositive: false })
      })
      return
    }
    this.setState({ notice: 'Could not copy. Select the preview and copy it manually.', noticePositive: false })
  }

  render() {
    const form = this.state.form
    const enums = wizardEnumOptions(store.api)
    const isConstruction = CONSTRUCTION_STATUSES.indexOf(form.securityTypeStatus) !== -1
    // Access so this observer re-renders after API Authentication / catalog load.
    void store.username
    void store.idToken
    void store.api
    return (
      <details className="proposal-submission-wizard" open>
        <summary className="proposal-submission-wizard__header">
          <span className="proposal-submission-wizard__title">
            {this.props.variant === 'lixi' ? 'LIXI Proposal Submission payload' : 'Proposal Submission payload'}
          </span>
          <small className="proposal-submission-wizard__hint">Funder ID and Customer ID come from the API login and cannot be edited.</small>
        </summary>
        <Form size="small">
          <Form.Group>
            <Form.Select
              width={4}
              compact
              label="Product"
              name="productName"
              options={enums.productName}
              value={form.productName}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="Channel"
              name="loanSourceChannel"
              options={enums.loanSourceChannel}
              value={form.loanSourceChannel}
              onChange={this.handleChange}
            />
            <Form.Field width={8} className="proposal-submission-wizard__checks">
              <label>Options</label>
              <div className="proposal-submission-wizard__checks-row">
                <Form.Checkbox
                  label="Guarantor"
                  name="includeGuarantor"
                  checked={!!form.includeGuarantor}
                  onChange={this.handleChange}
                />
              </div>
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Input
              width={4}
              label="Funder ID"
              name="funderId"
              value={form.funderId != null ? form.funderId : ''}
              readOnly
              placeholder="From API token"
              required
            />
            <Form.Input
              width={4}
              label="Originator ID"
              name="originatorId"
              value={form.originatorId}
              onChange={this.handleChange}
            />
            <Form.Input
              width={4}
              label="Customer ID"
              name="customerId"
              value={form.customerId != null ? form.customerId : ''}
              readOnly
              placeholder="Defaults to Funder ID"
            />
            <Form.Select
              width={4}
              compact
              label="Underwriting Type"
              name="underwritingType"
              options={[
                { key: 'STD', value: 'STD', text: 'Standard (STD)' },
                { key: 'DUA', value: 'DUA', text: 'Delegated (DUA)' }
              ]}
              value={form.underwritingType}
              onChange={this.handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Select
              width={4}
              compact
              label="Borrower"
              name="borrowerType"
              options={enums.borrowerType}
              value={form.borrowerType}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="Purpose"
              name="loanPurpose"
              options={enums.loanPurpose}
              value={form.loanPurpose}
              onChange={this.handleChange}
            />
            <Form.Input
              width={4}
              label="Loan amount"
              name="loanAmount"
              type="number"
              value={form.loanAmount}
              onChange={this.handleChange}
            />
            <Form.Input
              width={4}
              label="Term (months)"
              name="loanTermInMonths"
              type="number"
              value={form.loanTermInMonths}
              onChange={this.handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Select
              width={4}
              compact
              label="Occupancy"
              name="occupancyType"
              options={enums.occupancyType}
              value={form.occupancyType}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="Security"
              name="securityType"
              options={enums.securityType}
              value={form.securityType}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="Status"
              name="securityTypeStatus"
              options={enums.securityTypeStatus}
              value={form.securityTypeStatus}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="State"
              name="state"
              options={enums.state}
              value={form.state}
              onChange={this.handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Select
              width={4}
              compact
              label="Value kind"
              name="valueKind"
              options={[
                { key: 'purchasePrice', value: 'purchasePrice', text: 'Purchase price' },
                { key: 'estimatedValue', value: 'estimatedValue', text: 'Estimated value' }
              ]}
              value={form.valueKind}
              onChange={this.handleChange}
            />
            <Form.Input
              width={4}
              label="Value"
              name="securityValue"
              type="number"
              value={form.securityValue}
              onChange={this.handleChange}
            />
            <Form.Select
              width={4}
              compact
              label="Serviceability"
              name="serviceabilityType"
              options={enums.serviceabilityType}
              value={form.serviceabilityType}
              onChange={this.handleChange}
            />
            {isConstruction && (
              <Form.Input
                width={4}
                label="Project costs"
                name="projectCosts"
                type="number"
                value={form.projectCosts != null ? form.projectCosts : ''}
                onChange={this.handleChange}
                required
              />
            )}
          </Form.Group>
          <Form.Group>
            <Form.Select
              width={4}
              compact
              label="Valuation"
              name="valuationMode"
              options={[
                { key: 'inPrinciple', value: 'inPrinciple', text: 'In Principle' },
                { key: 'valex', value: 'valex', text: 'Valex' },
                { key: 'full', value: 'full', text: 'Full valuation' }
              ]}
              value={form.valuationMode}
              onChange={this.handleChange}
            />
            {form.valuationMode === 'valex' && (
              <Form.Input
                width={4}
                label="Valuation ID"
                name="valuationId"
                value={form.valuationId}
                onChange={this.handleChange}
                required
              />
            )}
            {form.valuationMode === 'full' && (
              <React.Fragment>
                <Form.Input
                  width={4}
                  label="Valuation date"
                  name="valuationDate"
                  type="date"
                  value={form.valuationDate || ''}
                  onChange={this.handleChange}
                />
                <Form.Input
                  width={4}
                  label="Valuer company"
                  name="valuerCompanyName"
                  value={form.valuerCompanyName || ''}
                  onChange={this.handleChange}
                />
              </React.Fragment>
            )}
          </Form.Group>
          <div className="proposal-submission-wizard__actions">
            <Button primary size="small" type="button" onClick={this.handleGenerate}>Generate</Button>
            <Button size="small" type="button" onClick={this.handleApply} disabled={!this.state.preview}>Apply to Try it out</Button>
            <Button size="small" type="button" onClick={this.handleCopy} disabled={!this.state.preview}>Copy JSON</Button>
          </div>
        </Form>
        {this.state.errors.length > 0 && (
          <Message negative size="tiny">
            {this.state.errors.map(function (err) { return <p key={err}>{err}</p> })}
          </Message>
        )}
        {this.state.notice && (
          <Message size="tiny" positive={this.state.noticePositive} negative={!this.state.noticePositive}>
            {this.state.notice}
          </Message>
        )}
        {this.state.preview && (
          <details className="proposal-submission-wizard__preview">
            <summary>Generated JSON</summary>
            <pre>{this.state.preview}</pre>
          </details>
        )}
      </details>
    )
  }
})
